/**
 * Socket hook factory that eliminates duplication across socket hooks.
 *
 * Handles the common lifecycle shared by most socket hooks:
 *   - Socket creation via socket.io-client `io()`
 *   - Token retrieval (localStorage or auth store)
 *   - Connection / disconnect / reconnect_failed state tracking
 *   - Debounced disconnect toast (3 s) with unmount suppression
 *   - Incident room join/leave (opt-in)
 *   - Event listener registration (stabilised via ref)
 *   - Cleanup on unmount
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useTenantStore } from '@/store/tenant.store';
import { useIncidentStore } from '@/store/incident.store';
import { useAuthStore } from '@/store/auth.store';
import { API_URL } from '@/services/api';
import { toast } from '@/utils/toast';
import { secureStorage } from '@/services/secure-storage.service';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TokenSource = 'localStorage' | 'authStore';

export interface SocketFactoryOptions {
  /** Socket.io namespace (e.g., '/tasks', '/board'). Empty string for default namespace. */
  namespace: string;

  /** Display name used in toast messages (e.g., 'Task', 'Board'). */
  displayName: string;

  /** Whether to enable the connection. Defaults to `true`. */
  enabled?: boolean;

  /** Whether to auto-manage incident room join/leave. Defaults to `false`. */
  incidentAware?: boolean;

  /** Whether to show toast notifications for connect/disconnect. Defaults to `true`. */
  showToasts?: boolean;

  /**
   * Base URL override. When not provided, `API_URL` from `services/api` is used.
   * Useful for hooks that compute their own URL (e.g. via `getApiBaseUrl()`).
   */
  baseUrl?: string;

  /**
   * Where to read the auth token from.
   * - `'localStorage'` (default): reads from the auth store, falling back to
   *   secure storage (`secureStorage.getSync('token')`). The auth store is the
   *   app's source of truth — the JWT is persisted inside the 'auth-storage'
   *   blob, not under a raw localStorage 'token' key.
   * - `'authStore'`: reads from a `token` value passed via `authToken`
   */
  tokenSource?: TokenSource;

  /**
   * When `tokenSource` is `'authStore'`, pass the token value here.
   * The factory treats a falsy value the same as "no token" and will skip connection.
   */
  authToken?: string | null;

  /** Additional auth params sent alongside the token (e.g. `{ tenantId }`). */
  extraAuth?: Record<string, string>;

  /**
   * Event listeners to register on the socket.
   * Key = event name, value = handler.
   * Handlers are stabilised via a ref so changes do NOT cause reconnection.
   */
  events?: Record<string, (...args: any[]) => void>;

  /** Called once the socket connects (useful for auto-joining custom rooms). */
  onConnect?: (socket: Socket) => void;

  /** Called when the socket disconnects. */
  onDisconnect?: (reason: string) => void;

  /** Called on `connect_error`. */
  onConnectError?: (error: Error) => void;

  /** Called when all reconnection attempts are exhausted. */
  onReconnectFailed?: () => void;

  /**
   * Called when the socket reconnects (after having been connected at least once).
   * Only fires when `showToasts` is true or when the consumer needs to know.
   */
  onReconnect?: (socket: Socket) => void;

  /** Extra socket.io connect options (merged into defaults). */
  socketOptions?: Record<string, unknown>;
}

export interface SocketFactoryReturn {
  /** Whether the socket is currently connected. */
  isConnected: boolean;

  /** Ref to the underlying socket instance (may be null). */
  socketRef: React.MutableRefObject<Socket | null>;

  /** Manually join an incident room. Only meaningful when `incidentAware` is true. */
  joinIncident: (incidentId: string) => void;

  /** Manually leave an incident room. Only meaningful when `incidentAware` is true. */
  leaveIncident: (incidentId: string) => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSocketFactory(options: SocketFactoryOptions): SocketFactoryReturn {
  const {
    namespace,
    displayName,
    enabled = true,
    incidentAware = false,
    showToasts = true,
    baseUrl,
    tokenSource = 'localStorage',
    authToken,
    extraAuth,
    socketOptions,
  } = options;

  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { activeTenant } = useTenantStore();

  // Incident room management (only used when incidentAware)
  const { activeIncident } = useIncidentStore();
  const currentIncidentRef = useRef<string | null>(null);

  // Stabilise all callback / event options via a ref so they never cause reconnection.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Track intentional unmount to suppress toasts during navigation
  const unmountingRef = useRef(false);
  // Track whether we have been connected at least once (avoid toast on first connect)
  const hasConnectedRef = useRef(false);
  // Debounce timer for the disconnect warning toast
  const disconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Resolve token value. For the default source, prefer the auth store — the
  // JWT is persisted inside the Zustand 'auth-storage' blob; no raw localStorage
  // 'token' key is ever written. Subscribing keeps this reactive so the socket
  // connects once login/rehydration populates the token.
  const storeToken = useAuthStore((s) => s.token);
  const resolvedToken =
    tokenSource === 'authStore'
      ? authToken
      : storeToken ?? secureStorage.getSync('token');

  // -----------------------------------------------------------------------
  // Main connection effect
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!enabled || !activeTenant) return;

    // Determine token
    const token = resolvedToken;

    if (!token) return;

    unmountingRef.current = false;

    const url = (baseUrl ?? API_URL) + (namespace ? `/${namespace.replace(/^\//, '')}` : '');

    // reconnectionAttempts MUST stay Infinity, and callers must not lower it.
    // With a finite budget socket.io's Manager emits `reconnect_failed` and then
    // never retries again for the life of the page (its only re-entry point,
    // onclose, cannot fire once the engine is closed). A ~30 s field outage
    // (cell/Starlink handoff) is enough to burn the budget, after which every
    // real-time feature on this origin is dead until a full reload — and because
    // socket.io caches ONE Manager per origin, whichever namespace happens to
    // create it first imposes its budget on /map, /forms, /board, /tasks and the
    // rest simultaneously. reconnectionDelayMax caps the backoff so a long
    // outage doesn't hammer the ingress.
    const socket = io(url, {
      auth: { token, ...optionsRef.current.extraAuth },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      ...optionsRef.current.socketOptions,
    });

    socketRef.current = socket;

    // -- Connection lifecycle ------------------------------------------------

    socket.on('connect', () => {
      setIsConnected(true);

      // Clear any pending disconnect toast timer
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }

      // Show reconnection toast only if we were previously connected
      if (optionsRef.current.showToasts !== false) {
        if (hasConnectedRef.current && !unmountingRef.current) {
          toast.success(
            displayName
              ? `${displayName} real-time updates restored`
              : 'Real-time updates restored',
          );
        }
      }

      const isReconnect = hasConnectedRef.current;
      hasConnectedRef.current = true;

      // Rejoin incident room on reconnect
      if (currentIncidentRef.current) {
        socket.emit('join:incident', currentIncidentRef.current);
      }

      // Notify consumer
      optionsRef.current.onConnect?.(socket);
      if (isReconnect) {
        optionsRef.current.onReconnect?.(socket);
      }
    });

    socket.on('disconnect', (reason: string) => {
      setIsConnected(false);

      // Debounce disconnect toast -- only show after 3 s of sustained disconnect
      if (optionsRef.current.showToasts !== false) {
        if (hasConnectedRef.current && !unmountingRef.current) {
          if (disconnectTimerRef.current) {
            clearTimeout(disconnectTimerRef.current);
          }
          disconnectTimerRef.current = setTimeout(() => {
            if (!unmountingRef.current && document.visibilityState !== 'hidden') {
              toast.warning(
                displayName
                  ? `${displayName} real-time updates disconnected`
                  : 'Real-time updates disconnected',
                'Attempting to reconnect...',
              );
            }
            disconnectTimerRef.current = null;
          }, 3000);
        }
      }

      optionsRef.current.onDisconnect?.(reason);
    });

    socket.on('connect_error', (error: Error) => {
      setIsConnected(false);
      optionsRef.current.onConnectError?.(error);
    });

    // Show persistent failure toast after all reconnection attempts exhausted.
    //
    // MUST be registered on the Manager (`socket.io`), NOT on the Socket.
    // socket.io-client v4 emits `reconnect_failed` only from Manager.reconnect();
    // the Socket forwards just open/packet/error/close from its Manager, so
    // `socket.on('reconnect_failed', ...)` is permanently dead code. If this
    // regresses, an EOC display that loses its backend connection (ingress
    // restart, WiFi flap) exhausts its 5 retries and then sits silently frozen:
    // no persistent "refresh the page" toast, and consumers that only learn of a
    // dead socket through `onReconnectFailed` (useCredentialSocket -> the
    // "Real-time updates unavailable" banner on Credentials Review) never warn
    // the operator, who then acts on stale data.
    //
    // The handler must be NAMED and removed by reference: socket.io-client
    // caches one Manager per origin, so every namespace on this host (/map,
    // /board, /tasks, ...) shares this listener list. An argument-less
    // `socket.io.off('reconnect_failed')` would strip every other hook's
    // listener as well.
    const onReconnectFailed = () => {
      if (optionsRef.current.showToasts !== false) {
        if (!unmountingRef.current && document.visibilityState !== 'hidden') {
          toast.error(
            displayName
              ? `${displayName} real-time updates unavailable`
              : 'Real-time updates unavailable',
            'Could not reconnect. Please refresh the page.',
          );
        }
      }
      optionsRef.current.onReconnectFailed?.();
    };
    socket.io.on('reconnect_failed', onReconnectFailed);

    // -- Domain event listeners -----------------------------------------------

    const currentEvents = optionsRef.current.events;
    if (currentEvents) {
      for (const [eventName, handler] of Object.entries(currentEvents)) {
        // Wrap handler so it always calls the latest ref value
        socket.on(eventName, (...args: any[]) => {
          optionsRef.current.events?.[eventName]?.(...args);
        });
      }
    }

    // -- Cleanup --------------------------------------------------------------

    return () => {
      unmountingRef.current = true;
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
      // Remove only OUR Manager listener (the Manager is shared across every
      // namespace on this origin) and do it before disconnecting.
      socket.io.off('reconnect_failed', onReconnectFailed);
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
    // We intentionally only depend on values that should trigger a full reconnect.
    // Callbacks and events are read from optionsRef inside handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, activeTenant, tokenSource, resolvedToken, namespace, baseUrl, displayName]);

  // -----------------------------------------------------------------------
  // Incident room management (auto-join active incident)
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!incidentAware) return;
    if (!socketRef.current || !isConnected) return;

    const socket = socketRef.current;
    const newIncidentId = activeIncident?.id || null;
    const oldIncidentId = currentIncidentRef.current;

    // Leave old incident room
    if (oldIncidentId && oldIncidentId !== newIncidentId) {
      socket.emit('leave:incident', oldIncidentId);
    }

    // Join new incident room
    if (newIncidentId && newIncidentId !== oldIncidentId) {
      socket.emit('join:incident', newIncidentId);
    }

    currentIncidentRef.current = newIncidentId;
  }, [incidentAware, activeIncident?.id, isConnected]);

  // -----------------------------------------------------------------------
  // Manual incident room helpers
  // -----------------------------------------------------------------------
  const joinIncident = useCallback(
    (incidentId: string) => {
      if (socketRef.current && isConnected) {
        socketRef.current.emit('join:incident', incidentId);
        currentIncidentRef.current = incidentId;
      }
    },
    [isConnected],
  );

  const leaveIncident = useCallback(
    (incidentId: string) => {
      if (socketRef.current && isConnected) {
        socketRef.current.emit('leave:incident', incidentId);
        if (currentIncidentRef.current === incidentId) {
          currentIncidentRef.current = null;
        }
      }
    },
    [isConnected],
  );

  return {
    isConnected,
    socketRef,
    joinIncident,
    leaveIncident,
  };
}
